import { Outlet, Link, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useState } from 'react'
import StarField from './StarField'
import BreezyLogo from './BreezyLogo'
import GeekModeToggle from './GeekModeToggle'
import GeekModePanel from './GeekModePanel'
import AccessibilityToggle from './AccessibilityToggle'
import { useGeekMode } from '../contexts/GeekModeContext'
import { useAccessibility } from '../contexts/AccessibilityContext'
import { useWorkflow } from '../contexts/WorkflowContext'

export default function Layout() {
  const { instance, accounts } = useMsal()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isPanelOpen } = useGeekMode()
  const { starfieldEnabled } = useAccessibility()
  const { isPreSales } = useWorkflow()

  const user = accounts[0]

  // Dynamic theme colors based on workflow
  const themeColors = isPreSales
    ? {
        accentGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(20, 184, 166, 0.2) 100%)',
        accentBorder: 'rgba(52, 211, 153, 0.3)',
        accentShadow: '0 0 20px rgba(16, 185, 129, 0.15)',
        accentColor: '#34d399',
        borderColor: 'rgba(52, 211, 153, 0.1)',
      }
    : {
        accentGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%)',
        accentBorder: 'rgba(96, 165, 250, 0.3)',
        accentShadow: '0 0 20px rgba(59, 130, 246, 0.15)',
        accentColor: '#60a5fa',
        borderColor: 'rgba(100, 180, 255, 0.1)',
      }

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    if (path === '/ea') return location.pathname === '/ea'
    return location.pathname.startsWith(path)
  }

  const navLinkClasses = (path: string) => {
    const active = isActive(path)
    return `flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
      active
        ? 'text-white'
        : 'text-slate-400 hover:text-slate-200'
    }`
  }

  const navLinkStyle = (path: string) => {
    const active = isActive(path)
    if (active) {
      return {
        background: themeColors.accentGradient,
        border: `1px solid ${themeColors.accentBorder}`,
        boxShadow: themeColors.accentShadow
      }
    }
    return {
      background: 'transparent',
      border: '1px solid transparent'
    }
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#080c18' }}>
      {starfieldEnabled && <StarField />}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 lg:hidden"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-72 flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          background: isPreSales ? 'rgba(6, 78, 59, 0.3)' : 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${themeColors.borderColor}`
        }}
      >
        {/* Logo */}
        <div className="p-6" style={{ borderBottom: `1px solid ${themeColors.borderColor}` }}>
          <Link to="/" className="block">
            <BreezyLogo size="md" />
          </Link>
          {/* Mobile close button */}
          <button
            className="lg:hidden absolute top-4 right-4 p-2 rounded-lg transition-colors"
            style={{ color: 'rgba(148, 163, 184, 0.8)' }}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <Link
            to="/"
            className={navLinkClasses('/')}
            style={navLinkStyle('/')}
          >
            <i className="fa-solid fa-th-large w-5" style={{ color: isActive('/') ? themeColors.accentColor : 'inherit' }}></i>
            <span>Dashboard</span>
          </Link>
          <Link
            to="/projects"
            className={navLinkClasses('/projects')}
            style={navLinkStyle('/projects')}
          >
            <i className="fa-solid fa-folder w-5" style={{ color: isActive('/projects') ? (isPreSales ? '#34d399' : '#a78bfa') : 'inherit' }}></i>
            <span>{isPreSales ? 'Opportunities' : 'Projects'}</span>
          </Link>
          <Link
            to="/recordings"
            className={navLinkClasses('/recordings')}
            style={navLinkStyle('/recordings')}
          >
            <i className="fa-solid fa-microphone w-5" style={{ color: isActive('/recordings') ? '#fbbf24' : 'inherit' }}></i>
            <span>{isPreSales ? 'Discovery Calls' : 'Transcripts & Audio'}</span>
          </Link>
          <Link
            to="/documents"
            className={navLinkClasses('/documents')}
            style={navLinkStyle('/documents')}
          >
            <i className="fa-solid fa-file-lines w-5" style={{ color: isActive('/documents') ? (isPreSales ? '#2dd4bf' : '#34d399') : 'inherit' }}></i>
            <span>{isPreSales ? 'SOW Documents' : 'Documents'}</span>
          </Link>

          {/* Pre-Sales specific nav items */}
          {isPreSales && (
            <>
              <div className="pt-4 pb-2">
                <div className="px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(52, 211, 153, 0.5)' }}>
                  Pre-Sales Tools
                </div>
              </div>
              <Link
                to="/sow-builder"
                className={navLinkClasses('/sow-builder')}
                style={navLinkStyle('/sow-builder')}
              >
                <i className="fa-solid fa-file-contract w-5" style={{ color: isActive('/sow-builder') ? '#34d399' : 'inherit' }}></i>
                <span>SOW Builder</span>
              </Link>
              <div
                className="flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-xl cursor-not-allowed opacity-50"
                style={{ color: 'rgba(148, 163, 184, 0.5)' }}
                title="Coming soon in Phase 3"
              >
                <i className="fa-solid fa-sitemap w-5"></i>
                <span>WBS Builder</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }}>Soon</span>
              </div>
            </>
          )}

          <div className="pt-4 pb-2">
            <div className="px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
              {isPreSales ? 'Content & Templates' : 'Templates & AI'}
            </div>
          </div>

          <Link
            to="/templates"
            className={navLinkClasses('/templates')}
            style={navLinkStyle('/templates')}
          >
            <i className="fa-solid fa-robot w-5" style={{ color: isActive('/templates') ? (isPreSales ? '#2dd4bf' : '#22d3ee') : 'inherit' }}></i>
            <span>AI Templates</span>
          </Link>
          <Link
            to="/og-templates"
            className={navLinkClasses('/og-templates')}
            style={navLinkStyle('/og-templates')}
          >
            <i className="fa-solid fa-file-lines w-5" style={{ color: isActive('/og-templates') ? '#f472b6' : 'inherit' }}></i>
            <span>OG Templates</span>
          </Link>
          <Link
            to="/prompts"
            className={navLinkClasses('/prompts')}
            style={navLinkStyle('/prompts')}
          >
            <i className="fa-solid fa-clipboard-list w-5" style={{ color: isActive('/prompts') ? '#818cf8' : 'inherit' }}></i>
            <span>Prompt Library</span>
          </Link>
          <Link
            to="/experiments"
            className={navLinkClasses('/experiments')}
            style={navLinkStyle('/experiments')}
          >
            <i className="fa-solid fa-flask w-5" style={{ color: isActive('/experiments') ? '#f97316' : 'inherit' }}></i>
            <span>A/B Experiments</span>
          </Link>
          <Link
            to="/best-practices"
            className={navLinkClasses('/best-practices')}
            style={navLinkStyle('/best-practices')}
          >
            <i className="fa-solid fa-book-open w-5" style={{ color: isActive('/best-practices') ? '#10b981' : 'inherit' }}></i>
            <span>Best Practices</span>
          </Link>

          {/* EA-Orchestrator section divider */}
          <div className="pt-4 pb-2">
            <div className="px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
              EA-Orchestrator
            </div>
          </div>

          <Link
            to="/ea"
            className={navLinkClasses('/ea')}
            style={navLinkStyle('/ea')}
          >
            <i className="fa-solid fa-microchip w-5" style={{ color: isActive('/ea') ? '#38bdf8' : 'inherit' }}></i>
            <span>EA Dashboard</span>
          </Link>
          <Link
            to="/ea/projects"
            className={navLinkClasses('/ea/projects')}
            style={navLinkStyle('/ea/projects')}
          >
            <i className="fa-solid fa-diagram-project w-5" style={{ color: isActive('/ea/projects') ? '#a78bfa' : 'inherit' }}></i>
            <span>EA Projects</span>
          </Link>
          <Link
            to="/ea/vault"
            className={navLinkClasses('/ea/vault')}
            style={navLinkStyle('/ea/vault')}
          >
            <i className="fa-solid fa-database w-5" style={{ color: isActive('/ea/vault') ? '#34d399' : 'inherit' }}></i>
            <span>Vendor Vault</span>
          </Link>
          <Link
            to="/ea/log"
            className={navLinkClasses('/ea/log')}
            style={navLinkStyle('/ea/log')}
          >
            <i className="fa-solid fa-scroll w-5" style={{ color: isActive('/ea/log') ? '#fbbf24' : 'inherit' }}></i>
            <span>Global Log</span>
          </Link>

          <div className="pt-6 pb-2">
            <div className="px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
              Resources
            </div>
          </div>

          <Link
            to="/help"
            className={navLinkClasses('/help')}
            style={navLinkStyle('/help')}
          >
            <i className="fa-solid fa-question-circle w-5" style={{ color: isActive('/help') ? themeColors.accentColor : 'inherit' }}></i>
            <span>Help & FAQ</span>
          </Link>
        </nav>

        {/* User */}
        <div className="p-4" style={{ borderTop: `1px solid ${themeColors.borderColor}` }}>
          <div
            className="flex items-center space-x-3 px-3 py-3 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: `1px solid ${themeColors.borderColor}`
            }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: themeColors.accentGradient,
                border: `1px solid ${themeColors.accentBorder}`
              }}
            >
              <i className="fa-solid fa-user" style={{ color: themeColors.accentColor }}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: '#e2e8f0' }}>
                {user?.name || 'User'}
              </p>
              <p className="text-xs truncate" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                {user?.username || ''}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-110"
              style={{ color: 'rgba(148, 163, 184, 0.6)' }}
              title="Sign out"
            >
              <i className="fa-solid fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className="flex-1 overflow-y-auto relative z-10 transition-all duration-300"
        style={{ marginRight: isPanelOpen ? '450px' : 0 }}
      >
        {/* Header */}
        <header
          className="sticky top-0 z-10"
          style={{
            background: isPreSales ? 'rgba(6, 78, 59, 0.3)' : 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${themeColors.borderColor}`
          }}
        >
          <div className="px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 -ml-2 rounded-lg transition-colors"
                style={{ color: 'rgba(148, 163, 184, 0.8)' }}
                onClick={() => setSidebarOpen(true)}
              >
                <i className="fa-solid fa-bars text-xl"></i>
              </button>

              {/* Search - Desktop */}
              <div className="hidden lg:block relative flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(30, 41, 59, 0.5)',
                    border: '1px solid rgba(100, 180, 255, 0.15)',
                    color: '#e2e8f0'
                  }}
                />
                <i className="fa-solid fa-search absolute left-3.5 top-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
              </div>

              {/* Right side */}
              <div className="flex items-center space-x-3">
                {/* Accessibility Toggle */}
                <AccessibilityToggle />
                {/* GeekMode Toggle */}
                <GeekModeToggle />
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* GeekMode Panel */}
      <GeekModePanel />
    </div>
  )
}
